import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../ui/input-otp';
import { toast } from 'sonner';
import api from '../../utils/api';
import { useCart } from '../../context/CartContext';
import { Loader2 } from 'lucide-react';

export const WhatsAppOtpLogin = ({ open, onOpenChange, onSuccess }) => {
  const [step, setStep] = useState('phone'); // 'phone' or 'otp'
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [devOtp, setDevOtp] = useState(''); // For development
  const { login } = useCart();

  const handleSendOtp = async () => {
    if (!phone || phone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setLoading(true);
    try {
      const formattedPhone = phone.startsWith('+91') ? phone : `+91${phone}`;
      const result = await api.sendOTP(formattedPhone);
      
      if (result.success) {
        toast.success('OTP sent to your WhatsApp!');
        if (result.otp_dev) {
          setDevOtp(result.otp_dev);
          toast.info(`Dev OTP: ${result.otp_dev}`);
        }
        setStep('otp');
      } else {
        toast.error(result.message || 'Failed to send OTP');
      }
    } catch (error) {
      toast.error('Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 6) {
      toast.error('Please enter a 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      const formattedPhone = phone.startsWith('+91') ? phone : `+91${phone}`;
      const result = await api.verifyOTP(formattedPhone, otp);
      
      if (result.success) {
        toast.success('Login successful!');
        login({ phone: result.phone, token: result.token });
        localStorage.setItem('tiffin_token', result.token);
        onSuccess && onSuccess(result);
        onOpenChange(false);
        setStep('phone');
        setPhone('');
        setOtp('');
      } else {
        toast.error(result.message || 'Invalid OTP');
      }
    } catch (error) {
      toast.error('Failed to verify OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-display">
            {step === 'phone' ? 'Login with WhatsApp' : 'Verify OTP'}
          </DialogTitle>
          <DialogDescription>
            {step === 'phone'
              ? 'Enter your phone number to receive an OTP on WhatsApp'
              : `Enter the 6-digit OTP sent to ${phone}`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {step === 'phone' ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <div className="flex gap-2">
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="9876543210"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="flex-1"
                    data-testid="whatsapp-login-phone-input"
                    disabled={loading}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  We'll send you a verification code on WhatsApp
                </p>
              </div>
              <Button
                className="w-full"
                onClick={handleSendOtp}
                disabled={loading}
                data-testid="whatsapp-login-send-otp-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send OTP'
                )}
              </Button>
            </>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="otp">Enter OTP</Label>
                <div className="flex justify-center">
                  <InputOTP
                    maxLength={6}
                    value={otp}
                    onChange={setOtp}
                    data-testid="whatsapp-login-otp-input"
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                {devOtp && (
                  <p className="text-xs text-center text-muted-foreground">
                    Development OTP: {devOtp}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setStep('phone');
                    setOtp('');
                  }}
                  disabled={loading}
                >
                  Back
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleVerifyOtp}
                  disabled={loading}
                  data-testid="whatsapp-login-verify-otp-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify'
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};